import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { PendingSample } from "@probetalab/contracts";
import { get, getHtml, run } from "../lib/api.js";
import { fmtAgo, fmtDate, fmtTime, plural, tubeName } from "../lib/format.js";
import { printHtml } from "../lib/print.js";
import { useBranch } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { Button, Card, Chip, Empty, Field, Input, Loading, PageHead, Select, Sheet, TubeSwatch } from "../components/ui.js";
import { DateRangeFilter, dateRangeQuery, describeDateRange, useDateRange } from "../components/DateRangeFilter.js";
import { Icon } from "../components/icons.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";

/** Tells the sidebar badge to recount after a tube changes state here. */
export const PENDING_SAMPLES_CHANGED = "probeta:pending-samples-changed";

/** Rejection reasons from the functional scope (S13); "Otro" asks for the text. */
export const REJECT_REASONS = ["Hemolizada", "Insuficiente", "Mal identificada", "Coagulada", "Recipiente incorrecto", "Derramada"] as const;
const OTHER = "Otro";

/**
 * Muestras pendientes (Very, 2026-11-02, "como en Toronja"): the branch's tubes still to be
 * collected — the patient brings the urine later, or a rejected tube waits for a new collection.
 * The day's by default, like Captura; a scan (tube code or folio) finds a tube on any day.
 */
export function PendingSamplesPage() {
  const branch = useBranch();
  const toast = useToast();
  const [range, setRange] = useDateRange();
  const dates = dateRangeQuery(range);
  const [scan, setScan] = useState("");
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<PendingSample | null>(null);
  const list = useQuery(
    () => get("pendingSamples", { query: { branch_id: branch.id, limit: 200, ...(code ? { code } : dates) } }),
    [branch.id, dates.day, dates.from, dates.to, code],
  );

  const groups = useMemo(() => {
    const m = new Map<string, PendingSample[]>();
    for (const s of list.data?.items ?? []) m.set(s.order_id, [...(m.get(s.order_id) ?? []), s]);
    return [...m.values()];
  }, [list.data]);
  const total = list.data?.items.length ?? 0;

  async function changed() {
    window.dispatchEvent(new Event(PENDING_SAMPLES_CHANGED));
    await list.reload();
  }

  async function collected(s: PendingSample) {
    setBusy(`collect:${s.sample_id}`);
    try {
      await run("sample.event", { sample_id: s.sample_id, kind: "collected", reason: null, to_branch_id: null }, { branch_id: branch.id });
      toast.success(`Muestra ${s.label_code} tomada.`);
      await changed();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function reprint(s: PendingSample) {
    setBusy(`label:${s.sample_id}`);
    try {
      printHtml(await getHtml("orderLabelsHtml", { params: { id: s.order_id }, query: { sample_id: s.sample_id } }));
      await run("labels.printed", { order_id: s.order_id, sample_ids: [s.sample_id] }, { branch_id: branch.id });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHead
        kicker={branch.name}
        title="Muestras pendientes"
        lede="Tubos por tomar o por volver a tomar. Escanea el código del tubo o el folio para encontrarlo."
      />
      <div className="toolbar">
        <form
          className="scan-bar grow"
          style={{ maxWidth: 560 }}
          onSubmit={(e) => {
            e.preventDefault();
            const c = scan.trim().toUpperCase();
            setCode(c || null);
            setScan("");
          }}
        >
          <Icon name="scan" />
          <input {...NO_AUTOFILL} type="search" enterKeyHint="go" value={scan} onChange={(e) => setScan(e.target.value)} placeholder="Escanear folio o código de tubo…" aria-label="Escanear folio o código de tubo" autoFocus autoComplete="off" />
          <Button size="sm" variant="ghost" type="submit">
            Buscar
          </Button>
        </form>
        {code ? (
          <Button variant="ghost" icon="x" onClick={() => setCode(null)}>
            Quitar búsqueda «{code}»
          </Button>
        ) : (
          <DateRangeFilter value={range} onChange={setRange} allowAny />
        )}
      </div>
      {list.loading && !list.data ? (
        <Loading />
      ) : list.error && !list.data ? (
        <Card>
          <Empty icon="warning" title="No se pudo cargar la lista" hint={list.error} />
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          {code ? (
            <Empty icon="dropper" title={`Sin muestras pendientes para ${code}`} hint="Revisa el código, o esa solicitud ya tiene todas sus muestras." />
          ) : (
            <Empty
              icon="dropper"
              title={range.preset === "any" ? "No hay muestras pendientes" : `No hay muestras pendientes ${describeDateRange(range)}`}
              hint={range.preset === "any" ? "Cuando una solicitud quede con un tubo por tomar, aparecerá aquí." : "Elige otras fechas o toca «Todos los días» para ver las de días anteriores."}
            />
          )}
        </Card>
      ) : (
        <div className="stack-lg">
          <p className="muted small" style={{ margin: 0 }}>
            {plural(total, "tubo por tomar", "tubos por tomar")} en {plural(groups.length, "solicitud", "solicitudes")}
          </p>
          {groups.map((tubes) => {
            const first = tubes[0]!;
            return (
              <Card
                key={first.order_id}
                pad={false}
                title={
                  <div className="row wrap" style={{ gap: 12 }}>
                    <Link to={`/solicitudes/${first.order_id}`} className="folio" style={{ textDecoration: "none" }}>
                      {first.order_folio}
                    </Link>
                    <span style={{ fontWeight: 600 }}>{first.patient_name}</span>
                    <span className="muted small">{first.patient_age_text}</span>
                    <span className="muted small" title={`Registrada el ${fmtDate(first.registered_at)} a las ${fmtTime(first.registered_at)}`}>
                      {fmtAgo(first.registered_at)} · {dates.day && !code ? fmtTime(first.registered_at) : `${fmtDate(first.registered_at)}, ${fmtTime(first.registered_at)}`}
                    </span>
                    {first.fasting_hours !== null && first.fasting_hours > 0 && <Chip tone="amber">Ayuno {first.fasting_hours} h</Chip>}
                    {first.condition && <Chip tone="neutral">{first.condition}</Chip>}
                  </div>
                }
                actions={
                  <Link to={`/solicitudes/${first.order_id}`} className="btn btn-ghost btn-sm">
                    Ver solicitud
                    <Icon name="chevronRight" />
                  </Link>
                }
              >
                {tubes.map((s) => (
                  <div key={s.sample_id} className="list-row wrap" data-testid={`tube-${s.label_code}`}>
                    <TubeSwatch hex={s.container_colour_hex} name={tubeName(s)} />
                    <span className="mono small muted">{s.label_code}</span>
                    <span className="grow">
                      <div className="primary">{s.study_claves.join(", ")}</div>
                      {s.indications.length > 0 && <div className="secondary small">{s.indications.join(" · ")}</div>}
                    </span>
                    <span className="row wrap" style={{ gap: 8, marginLeft: "auto", justifyContent: "flex-end" }}>
                      {s.state === "rejected" ? (
                        <Chip tone="amber" dot>
                          Rechazada{s.rejected_reason ? `: ${s.rejected_reason}` : ""} · nueva toma
                        </Chip>
                      ) : (
                        <Chip tone="red" dot>
                          Pendiente
                        </Chip>
                      )}
                      <Button size="sm" variant="primary" icon="dropper" busy={busy === `collect:${s.sample_id}`} onClick={() => void collected(s)}>
                        Muestra tomada
                      </Button>
                      <Button size="sm" variant="ghost" icon="tag" busy={busy === `label:${s.sample_id}`} onClick={() => void reprint(s)}>
                        Reimprimir etiqueta
                      </Button>
                      {s.state === "pending" && (
                        <Button size="sm" variant="ghost" icon="x" onClick={() => setRejecting(s)}>
                          Rechazar
                        </Button>
                      )}
                    </span>
                  </div>
                ))}
              </Card>
            );
          })}
        </div>
      )}
      <RejectSheet sample={rejecting} onClose={() => setRejecting(null)} onDone={() => void changed()} />
    </>
  );
}

/** The confirm step of a rejection: a reason from the list (or written), then one more tap. */
function RejectSheet({ sample, onClose, onDone }: { sample: PendingSample | null; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const branch = useBranch();
  const [choice, setChoice] = useState<string>("");
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);
  const reason = choice === OTHER ? other.trim() : choice;

  function close() {
    setChoice("");
    setOther("");
    onClose();
  }

  async function confirm() {
    if (!sample || !reason) return;
    setBusy(true);
    try {
      await run("sample.event", { sample_id: sample.sample_id, kind: "rejected", reason, to_branch_id: null }, { branch_id: branch.id });
      toast.success(`Muestra ${sample.label_code} rechazada. Queda en la lista para una nueva toma.`);
      onDone();
      close();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={sample !== null}
      onClose={close}
      title={sample ? `Rechazar ${sample.label_code}` : "Rechazar muestra"}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Conservar
          </Button>
          <Button variant="danger" icon="x" busy={busy} disabled={!reason} onClick={() => void confirm()}>
            Rechazar muestra
          </Button>
        </>
      }
    >
      <p className="small muted">El rechazo queda en la bitácora con tu nombre y el motivo. El tubo sigue en esta lista hasta que se registre una nueva toma.</p>
      <Field label="Motivo">
        <Select value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="Motivo del rechazo">
          <option value="">Elige un motivo</option>
          {REJECT_REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
          <option value={OTHER}>{OTHER}</option>
        </Select>
      </Field>
      {choice === OTHER && (
        <Field label="¿Cuál?">
          <Input value={other} onChange={(e) => setOther(e.target.value)} placeholder="Describe el motivo" autoFocus aria-label="Otro motivo" />
        </Field>
      )}
    </Sheet>
  );
}
